import React, { useEffect, useState } from "react";
import {
    Box,
    Grid,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    IconButton,
    Typography,
    Popover,
    Card,
    CardContent,
    Checkbox,
    FormControlLabel,
    Dialog,
    DialogContent,
    DialogTitle,
    DialogActions,
    Button,
    Avatar,
    Chip,
    CircularProgress,
    Alert,
    Snackbar,
    alpha
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import RefreshIcon from "@mui/icons-material/Refresh";
import { vendorApi } from "../../api/api";

const SubscriptionsTable = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [screenshotOpen, setScreenshotOpen] = useState(false);
    const [selectedScreenshot, setSelectedScreenshot] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [anchorEl, setAnchorEl] = useState(null);
    const [pageSize, setPageSize] = useState(25);
    const [statusFilter, setStatusFilter] = useState("All");
    const [paymentFilter, setPaymentFilter] = useState("All");
    const [planFilter, setPlanFilter] = useState("All");
    const [selectedFields, setSelectedFields] = useState([]);
    const [actionLoading, setActionLoading] = useState({});
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });

    useEffect(() => {
        fetchSubscriptions();
    }, []);

    const fetchSubscriptions = async () => {
        try {
            setLoading(true);
            const res = await vendorApi.getAllSubscriptions();
            

            // Check if data exists and extract it properly
            const data = res.data || [];
           

            // Transform data to match the required structure
            const transformedData = data.map((item, index) => {
               
                if (item.subscription_id) {
                    // Data is already in correct format
                    return {
                        id: item.subscription_id || index + 1,
                        subscription_id: item.subscription_id,
                        username: item.username || "N/A",
                        name: item.name || "N/A",
                        period: item.period || "N/A",
                        amount: item.amount || 0,
                        start_date: item.start_date || new Date().toISOString(),
                        end_date: item.end_date || new Date().toISOString(),
                        screenshot_url: item.screenshot_url || null,
                        subscription_status: item.subscription_status || "pending",
                        payment_status: item.payment_status || null,
                        user_id: item.user_id,
                        paymentId: item.paymentId,
                        userMobile: item.Mobilenumber || "N/A",
                        userEmail: item.Email || "N/A"
                    };
                } else {
                    // Data is in the raw format from your example
                    return {
                        id: item.subscription_id || index + 1,
                        subscription_id: item.subscription_id,
                        username: item.username || "N/A",
                        name: item.name || "N/A",
                        period: item.period || "N/A",
                        amount: item.amount || 0,
                        start_date: item.start_date || new Date().toISOString(),
                        end_date: item.end_date || new Date().toISOString(),
                        screenshot_url: item.screenshot_url || null,
                        subscription_status: item.subscription_status || "Pending",
                        payment_status: item.payment_status || null,
                        user_id: item.user_id,
                        paymentId: item.paymentId,
                        userMobile: item.userMobile || "N/A",
                        userEmail: item.userEmail || "N/A"
                    };
                }
            });

       
            setRows(transformedData);

            // Initialize selected fields with all column fields
            const allFields = columns.map(col => col.field);
            setSelectedFields(allFields);
        } catch (err) {
            console.error("Failed to fetch subscriptions:", err);
            showSnackbar("Failed to load subscriptions", "error");
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    const updateStatus = async (row, newStatus) => {
        try {
            setActionLoading(prev => ({ ...prev, [row.id]: true }));

            const payload = {
                userId: row.user_id,
                paymentId: row.paymentId,
                status: newStatus
            };

            await vendorApi.approveSubscription(payload);

            // update UI
            setRows(prev =>
                prev.map(r =>
                    r.id === row.id
                        ? {
                            ...r,
                            subscription_status: newStatus,
                            payment_status: newStatus
                        }
                        : r
                )
            );

            showSnackbar(`Status updated to ${newStatus}`, "success");
        } catch (err) {
            console.error("Status update failed:", err);
            showSnackbar("Failed to update status", "error");
        } finally {
            setActionLoading(prev => ({ ...prev, [row.id]: false }));
        }
    };


    //   const openScreenshot = (url) => {
    //     if (url && url !== "0" && url !== "null") {
    //       setSelectedScreenshot(url);
    //       setScreenshotOpen(true);
    //     } else {
    //       showSnackbar("No screenshot available", "info");
    //     }
    //   };

    const closeScreenshot = () => {
        setSelectedScreenshot(null);
        setScreenshotOpen(false);
    };

    const showSnackbar = (message, severity = "success") => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === "clickaway") return;
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const handleIconClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };



    const getUserAvatar = (username) => {
        return username?.charAt(0).toUpperCase() || "U";
    };

    const getAvatarColor = (username) => {
        const colors = [
            "#1976d2",
            "#dc004e",
            "#388e3c",
            "#f57c00",
            "#7b1fa2",
            "#0288d1",
        ];
        const index = username?.length % colors.length || 0;
        return colors[index];
    };

    const formatDate = (dateString) => {
        if (!dateString || dateString === "null") return "N/A";
        try {
            return new Date(dateString).toLocaleDateString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric"
            });
        } catch (error) {
            console.error("Date formatting error:", error);
            return "Invalid Date";
        }
    };

    // Filter logic
    const filteredData = rows.filter((row) => {
        const matchesSearch = searchQuery === "" ||
            Object.values(row).join(" ").toLowerCase().includes(searchQuery.toLowerCase());

        const matchesStatus = statusFilter === "All" ||
            (row.subscription_status?.toLowerCase() || "").includes(statusFilter.toLowerCase());

        const matchesPayment = paymentFilter === "All" ||
            (row.payment_status?.toLowerCase() || "pending").includes(paymentFilter.toLowerCase());

        const matchesPlan = planFilter === "All" ||
            (row.name?.toLowerCase() || "").includes(planFilter.toLowerCase());

        return matchesSearch && matchesStatus && matchesPayment && matchesPlan;
    });

    // Define columns
    const columns = [
        {
            field: "id",
            headerName: "ID",
            width: 60,
            renderCell: (params) => (
                <Typography variant="body2" sx={{ color: "#666" }}>
                    {params.row.subscription_id || params.value}
                </Typography>
            )
        },
        {
            field: "username",
            headerName: "User",
            width: 180,
            renderCell: (params) => (
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Avatar
                        sx={{
                            bgcolor: getAvatarColor(params.value),
                            width: 32,
                            height: 32,
                            fontSize: "0.875rem"
                        }}
                    >
                        {getUserAvatar(params.value)}
                    </Avatar>
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: "medium" }}>
                            {params.value || "N/A"}
                        </Typography>
                    </Box>
                </Box>
            ),
        },
        {
            field: "contact_info",
            headerName: "Contact Info",
            width: 220,
            renderCell: (params) => (
                <Box>
                    <Typography variant="body2" sx={{ color: "#1976d2" }}>
                        {params.row.userMobile || "N/A"}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#666", display: 'block' }}>
                        {params.row.userEmail || "N/A"}
                    </Typography>
                </Box>
            ),
        },
        {
            field: "name",
            headerName: "Plan",
            width: 150,
            renderCell: (params) => (
                <Chip
                    label={params.value || "N/A"}
                    size="small"
                    sx={{
                        backgroundColor: "#e3f2fd",
                        color: "#1976d2",
                        border: "1px solid #bbdefb"
                    }}
                />
            ),
        },
        {
            field: "period",
            headerName: "Period",
            width: 100,
            renderCell: (params) => (
                <Typography variant="body2" sx={{ color: "#666" }}>
                    {params.value || "N/A"}
                </Typography>
            ),
        },
        {
            field: "amount",
            headerName: "Amount",
            width: 120,
            renderCell: (params) => (
                <Typography variant="body2" sx={{ fontWeight: "bold", color: "#2e7d32" }}>
                    ₹{params.value || "0"}
                </Typography>
            ),
        },
        {
            field: "start_date",
            headerName: "Start Date",
            width: 150,
            renderCell: (params) => formatDate(params.value),
        },
        {
            field: "end_date",
            headerName: "End Date",
            width: 150,
            renderCell: (params) => formatDate(params.value),
        },
        // {
        //   field: "screenshot_url",
        //   headerName: "Screenshot",
        //   width: 130,
        //   renderCell: (params) => (
        //     params.value && params.value !== "0" && params.value !== "null" ? (
        //       <IconButton
        //         onClick={() => openScreenshot(params.value)}
        //         size="small"
        //         sx={{
        //           backgroundColor: "#e3f2fd",
        //           '&:hover': {
        //             backgroundColor: "#bbdefb",
        //           }
        //         }}
        //       >
        //         <VisibilityIcon fontSize="small" sx={{ color: "#1976d2" }} />
        //       </IconButton>
        //     ) : (
        //       <Typography variant="body2" sx={{ color: "#999" }}>
        //         No image
        //       </Typography>
        //     )
        //   ),
        // },
        {
            field: "subscription_status",
            headerName: "Status",
            width: 150,
            renderCell: (params) => {
                const status = params.value?.toLowerCase() || "Pending";
                let chip;

                if (status === "active") {
                    chip = <Chip label="Approved" color="success" size="small" />;
                } else if (status === "rejected") {
                    chip = <Chip label="Rejected" color="error" size="small" />;
                } else if (status === "pending") {
                    chip = <Chip label="Pending" color="warning" size="small" />;
                } else {
                    chip = <Chip label={status.charAt(0).toUpperCase() + status.slice(1)} size="small" />;
                }

                return chip;
            },
        },
        // {
        //   field: "payment_status",
        //   headerName: "Payment Status",
        //   width: 150,
        //   renderCell: (params) => {
        //     const status = params.value?.toLowerCase() || "pending";
        //     let chip;

        //     if (status === "paid") {
        //       chip = <Chip label="Paid" color="success" size="small" />;
        //     } else if (status === "pending") {
        //       chip = <Chip label="Pending" color="warning" size="small" />;
        //     } else if (status === "rejected") {
        //       chip = <Chip label="Rejected" color="error" size="small" />;
        //     } else if (status === "refunded") {
        //       chip = <Chip label="Refunded" color="info" size="small" />;
        //     } else if (status === "null" || !status) {
        //       chip = <Chip label="Not Set" size="small" />;
        //     } else {
        //       chip = <Chip label={status.charAt(0).toUpperCase() + status.slice(1)} size="small" />;
        //     }

        //     return chip;
        //   },
        // },
        {
            field: "change_status",
            headerName: "Change Status",
            width: 180,
            renderCell: (params) => (
                <Box sx={{ position: 'relative' }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <Select
                            value={params.row.subscription_status || "Pending"}
                            onChange={(e) => updateStatus(params.row, e.target.value)}
                            disabled={actionLoading[params.row.id]}
                            size="small"
                        >
                            <MenuItem value="Approved">Approved</MenuItem>
                            <MenuItem value="Rejected">Rejected</MenuItem>
                        </Select>

                    </FormControl>
                    {actionLoading[params.row.id] && (
                        <CircularProgress
                            size={20}
                            sx={{
                                position: 'absolute',
                                right: 40,
                                top: '50%',
                                transform: 'translateY(-50%)'
                            }}
                        />
                    )}
                </Box>
            ),
        },
    ];

    const displayedColumns = columns.filter((col) => selectedFields.includes(col.field));
    const open = Boolean(anchorEl);
    const id = open ? "column-popover" : undefined;

    // Stats calculation
    const stats = {
        total: rows.length,
        approved: rows.filter(row =>
            row.subscription_status?.toLowerCase() === "active").length,
        pending: rows.filter(row =>
            row.subscription_status?.toLowerCase() === "pending").length,
        rejected: rows.filter(row =>
            row.subscription_status?.toLowerCase() === "rejected").length,
    };

    if (loading) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "400px",
                }}
            >
                <Box sx={{ textAlign: "center" }}>
                    <CircularProgress size={60} sx={{ mb: 2 }} />
                    <Typography variant="h6">
                        Loading Subscriptions...
                    </Typography>
                </Box>
            </Box>
        );
    }

    return (
        <>
            <Box>
                <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1, mt: 1 }}>
                    Vendor Subscriptions Management
                </Typography>
                <Typography variant="body1" sx={{ color: "text.secondary", mb: 3 }}>
                    Manage and monitor all vendor subscription activities
                </Typography>
            </Box>

            {/* Stats Cards */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={3}>
                    <Card sx={{
                        p: 2,
                        backgroundColor: "#1976d2",
                        color: "white",
                        borderRadius: 2
                    }}>
                        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                            {stats.total}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            Total Subscriptions
                        </Typography>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={3}>
                    <Card sx={{
                        p: 2,
                        backgroundColor: alpha("#1976d2", 0.8),
                        color: "white",
                        borderRadius: 2
                    }}>
                        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                            {stats.approved}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            Approved
                        </Typography>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={3}>
                    <Card sx={{
                        p: 2,
                        backgroundColor: alpha("#1976d2", 0.8),
                        color: "white",
                        borderRadius: 2
                    }}>
                        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                            {stats.pending}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            Pending
                        </Typography>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={3}>
                    <Card sx={{
                        p: 2,
                        backgroundColor: alpha("#1976d2", 0.8),
                        color: "white",
                        borderRadius: 2
                    }}>
                        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                            {stats.rejected}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            Rejected
                        </Typography>
                    </Card>
                </Grid>
            </Grid>

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    mt: 3,
                    overflowX: "hidden",
                    width: "100%",
                }}
            >
                <div style={{ width: '100%' }}>
                    <Card
                        sx={{
                            flexGrow: 1,
                            border: "1px solid lightgray",
                            borderRadius: "5px",
                        }}
                    >
                        <CardContent>
                            <Grid
                                container
                                spacing={2}
                                alignItems="center"
                                direction="row"
                                sx={{ width: "100%" }}
                            >
                                {/* Search Field */}
                                <Grid item xs={12} sm={6} md={3}>
                                    <TextField
                                        label="Search By Key words"
                                        variant="outlined"
                                        fullWidth
                                        size="small"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        InputProps={{
                                            startAdornment: (
                                                <SearchIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
                                            ),
                                        }}
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: "15px",
                                            },
                                        }}
                                    />
                                </Grid>

                                {/* Plan Filter */}
                                <Grid item xs={12} sm={6} md={2}>
                                    <TextField
                                        label="Search Plan"
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        value={planFilter === "All" ? "" : planFilter}
                                        onChange={(e) => setPlanFilter(e.target.value || "All")}
                                        sx={{
                                            "& .MuiOutlinedInput-root": {
                                                borderRadius: "15px",
                                            },
                                        }}
                                    />
                                </Grid>

                                {/* Status Filter */}
                                <Grid item xs={12} sm={6} md={2}>
                                    <FormControl variant="outlined" size="small" fullWidth>
                                        <InputLabel>Status</InputLabel>
                                        <Select
                                            value={statusFilter}
                                            onChange={(e) => setStatusFilter(e.target.value)}
                                            label="Status"
                                        >
                                            <MenuItem value="All">All</MenuItem>
                                            <MenuItem value="approved">Approved</MenuItem>
                                            <MenuItem value="pending">Pending</MenuItem>
                                            <MenuItem value="rejected">Rejected</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* Payment Status Filter */}
                                <Grid item xs={12} sm={6} md={2}>
                                    <FormControl variant="outlined" size="small" fullWidth>
                                        <InputLabel>Payment Status</InputLabel>
                                        <Select
                                            value={paymentFilter}
                                            onChange={(e) => setPaymentFilter(e.target.value)}
                                            label="Payment Status"
                                        >
                                            <MenuItem value="All">All</MenuItem>
                                            <MenuItem value="paid">Paid</MenuItem>
                                            <MenuItem value="pending">Pending</MenuItem>
                                            <MenuItem value="rejected">Rejected</MenuItem>
                                            <MenuItem value="refunded">Refunded</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* Action Buttons */}
                                <Grid item xs={12} sm={6} md={3} sx={{ display: 'flex', gap: 1 }}>

                                    <IconButton onClick={handleIconClick} sx={{ ml: 'auto' }}>
                                        <ViewColumnIcon />
                                    </IconButton>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    <Card style={{ height: "100vh", marginTop: "10px", backgroundColor: "#f4f1f1ff" }}>
                        <DataGrid
                            rows={filteredData}
                            columns={displayedColumns}
                            disableSelectionOnClick
                            pagination
                            pageSize={pageSize}
                            rowsPerPageOptions={[25, 50, 100]}
                            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                            loading={loading}
                            sx={{
                                height: 650,
                                border: '1px solid #ccc',
                                borderRadius: 2,
                                fontSize: '0.9rem',
                                backgroundColor: '#ffffff',
                                '& .MuiDataGrid-row:hover': {
                                    backgroundColor: '#f0f0f0ff',
                                },
                                '& .MuiDataGrid-cell': {
                                    color: '#333',
                                    borderBottom: '1px solid #ddd',
                                },
                                '& .MuiDataGrid-columnHeaders': {
                                    backgroundColor: '#f0f0f0ff',
                                    color: 'black',
                                    fontWeight: 'bold',
                                },
                                '& .MuiTablePagination-root': {
                                    fontSize: '0.875rem',
                                },
                            }}
                        />
                    </Card>
                </div>

                {/* Column Selection Popover */}
                <Popover
                    id={id}
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right",
                    }}
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                    }}
                >
                    <Box sx={{ p: 2, maxWidth: 300 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            Select Columns
                        </Typography>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                            {columns.map((col) => (
                                <FormControl
                                    key={col.field}
                                    component="fieldset"
                                    sx={{ m: 0, p: 0 }}
                                >
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={selectedFields.includes(col.field)}
                                                onChange={() => {
                                                    setSelectedFields((prev) =>
                                                        prev.includes(col.field)
                                                            ? prev.filter((f) => f !== col.field)
                                                            : [...prev, col.field]
                                                    );
                                                }}
                                                sx={{
                                                    '&.Mui-checked': {
                                                        color: 'skyblue',
                                                    },
                                                }}
                                            />
                                        }
                                        label={col.headerName}
                                        sx={{ m: 0 }}
                                    />
                                </FormControl>
                            ))}
                        </Box>
                    </Box>
                </Popover>

                {/* Screenshot Dialog */}
                <Dialog
                    open={screenshotOpen}
                    onClose={closeScreenshot}
                    maxWidth="md"
                    fullWidth
                >
                    <DialogTitle sx={{
                        backgroundColor: "#1976d2",
                        color: "white",
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        Payment Screenshot
                        <IconButton onClick={closeScreenshot} sx={{ color: "white" }}>
                            ✕
                        </IconButton>
                    </DialogTitle>
                    <DialogContent sx={{ p: 3 }}>
                        {selectedScreenshot ? (
                            <img
                                src={selectedScreenshot}
                                alt="Payment Screenshot"
                                style={{
                                    width: "100%",
                                    borderRadius: 8,
                                    maxHeight: "70vh",
                                    objectFit: "contain"
                                }}
                            />
                        ) : (
                            <Box sx={{ textAlign: "center", py: 4 }}>
                                <Typography variant="h6" color="textSecondary">
                                    No screenshot available
                                </Typography>
                            </Box>
                        )}
                    </DialogContent>
                </Dialog>

                {/* Snackbar for notifications */}
                <Snackbar
                    open={snackbar.open}
                    autoHideDuration={3000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: "top", horizontal: "right" }}
                >
                    <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                        {snackbar.message}
                    </Alert>
                </Snackbar>
            </Box>
        </>
    );
};

export default SubscriptionsTable;